// The season summary card (ui-map-interface.md §3.4, R1518, R1519, R2089).
//
// The card that closes a season on the game page. The hub sends a
// `SeasonSummary` when the scores lock at seaquake stage 3, again to a
// commander who joins while they stand locked, and again when the
// calendar's hold changes. The card opens when it arrives and stands over
// the map until the player puts it away (VIEW MAP or esc), leaves for the
// hub (MENU), or the season rolls: a roll brings a new hello, and a hello
// empties the summary (`state.ts setHello`).
//
// The card reads "your clan in the sea" (R2089). The head is the season
// banner's Abyss look at a third of its size, a reprise of the loading
// screen art (R2111): the mode and the world, the season, the glow rule
// and the clock line. The body then reads in this order:
//
//   1. YOU CARRY: what crosses the reset (numbers.md §0): the Honour the
//      roll pays, the captains that cross and the captains lost. A guest
//      that the roll deletes reads the guest sentence in its place, and
//      REGISTER stands in the footer (§3.5, R1565).
//   2. YOUR PLACE IN THE SEA: the place of the reader's faction, clan and
//      own row, then the duel against the winner only when the winner
//      sails in another faction.
//   3. The rankings and the sections in columns: on a desktop side by
//      side, in the sheet form factor stacked in one column that scrolls.
//   4. The season timelapse (card 07 of mockup round 076, R1620), which
//      `timelapse/summary.ts` keeps.
//
// A world that rolls counts down to the new season. A world the calendar
// holds (R613) states that the season is over and counts nothing.
//
// The first half of the file is the view model, pure and tested with no
// DOM. The class below it is the only part that touches a document.

import type {
  Captain, SeasonSummary, SeasonSummaryBanner, SeasonSummaryStats, Standing,
} from '../gen/proto/aquaphobia';
import {
  banner as B, guestWarn as G, overlay as O, seasonSummary as S,
} from '../gen/strings';
import {
  ACHIEVEMENT_GRADES, CHART, KEYS, SEASON_SUMMARY_ACH_ROWS, SEASON_SUMMARY_CARRY_ROWS,
  SEASON_SUMMARY_MEDAL_FIGURE_PX, SEASON_SUMMARY_MEDAL_PX, SEASON_SUMMARY_RANK_ROWS,
} from '../gen/presentation';
import { matches } from '../keys';
import type { AccountAchievements } from '../net/api';
import { reducedMotion } from '../render/motion';
import type { GameState } from '../state';
import { factionName } from '../world';
import { esc } from './esc';
import { cssColor } from './format';
import { hmmss } from '../locale/clock';
import { gradeName, nameOf } from './unlock';
import { rowByCode, zoneOf } from './achievements';
import { groupThousands } from '../locale/group';
import { phaseWord } from './overlay/caprow';
import { PressRoute } from './pressroute';
import { SummaryTimelapse } from './timelapse/summary';
import { cupMarkFor } from './cupmark';
import { MarkupHost } from './markuphost';

// ---------------------------------------------------------------------------
// The view model
// ---------------------------------------------------------------------------

/** The wire's mode numbers (`Hello.mode`): the ticket's order in
 *  `data/contracts/modes.toml`. */
const MODE_EVENT_ID = 1;
const MODE_CLANWAR_ID = 2;
const MODE_CUSTOM_ID = 4;

/** The name of one mode, as the kicker of the card reads it. */
export function modeName(mode: number): string {
  if (mode === MODE_EVENT_ID) return S.mode_event;
  if (mode === MODE_CLANWAR_ID) return S.mode_clanwar;
  if (mode === MODE_CUSTOM_ID) return S.mode_custom;
  return S.mode_core;
}

/** A custom world leaves no player record (R2979): it pays no Honour and
 *  files no achievement, so its card states neither. */
function traceFree(mode: number): boolean {
  return mode === MODE_CUSTOM_ID;
}

/** The kicker of the small banner: the mode, and the world when the hello
 *  names one. It stands over the hero, as on the loading screen (R2111). */
export function kickLine(mode: number, world: string): string {
  const w = world.trim();
  return w === '' ? modeName(mode) : S.kick({ mode: modeName(mode), world: w });
}

/**
 * The countdown to the new season: M:SS under one hour, H:MM:SS from one
 * hour on. It rounds UP, so the card never reads 0:00 while the old
 * season still stands.
 */
export function summaryCountdown(sec: number): string {
  const s = Math.max(0, Math.ceil(isFinite(sec) ? sec : 0));
  return hmmss(s);
}

/**
 * The wall seconds left to the new season, or null for a world the
 * calendar holds.
 *
 * The scoreboard's settlement deadline wins when it stands (`boardEndsMs`
 * over 0): every board re-anchors it, so a retune of the world clock moves
 * it. The summary's own span, anchored at its arrival, is the fallback.
 */
export function nextSeasonLeftSec(
  s: SeasonSummary, atMs: number, boardEndsMs: number, nowMs: number,
): number | null {
  if (s.held) return null;
  const endsMs = boardEndsMs > 0 ? boardEndsMs : atMs + s.nextSeasonInSec * 1000;
  return Math.max(0, (endsMs - nowMs) / 1000);
}

/** The clock line: the countdown, the line at zero, or the line of a held
 *  world. `mark` wraps the countdown figure; the card sets it in the
 *  accent. */
export function clockLine(leftSec: number | null, mark = (t: string): string => t): string {
  if (leftSec === null) return S.held_line;
  if (leftSec <= 0) return S.next_now;
  return S.next_in({ left: mark(summaryCountdown(leftSec)) });
}

/** The ending of an ordinal rank in the reader's language: 1st, 12th. */
export function ordinalSuffix(n: number): string {
  const v = Math.abs(Math.floor(n)) % 100;
  const [th, st, nd, rd] = S.ordinal;
  if (v >= 11 && v <= 13) return th;
  switch (v % 10) {
    case 1: return st;
    case 2: return nd;
    case 3: return rd;
    default: return th;
  }
}

/** An ordinal rank inline: `3rd`, or `3.` in German (R2089). The ending
 *  stands at the size of the figure, never raised. */
export function ordinal(n: number): string {
  return `${n}${ordinalSuffix(n)}`;
}

/** A gap as the card writes it: a plus, a true minus, or ± for none. */
export function signedFigure(n: number): string {
  if (n === 0) return `±${groupThousands(0)}`;
  return `${n > 0 ? '+' : '−'}${groupThousands(Math.abs(n))}`;
}

/** One line of the ranking. `own` marks the reader's line; `gap` marks the
 *  reader's line when it stands below the top rows. */
export interface RankLine {
  row: Standing;
  own: boolean;
  gap: boolean;
}

/**
 * The ranking the card states: the top `top` rows of the locked table, and
 * the reader's own row under them when it is not among them.
 */
export function rankLines(
  rows: readonly Standing[], you: Standing | undefined, top = SEASON_SUMMARY_RANK_ROWS,
): RankLine[] {
  const own = you?.accountId ?? -1;
  const out: RankLine[] = rows.slice(0, top).map((row) => ({
    row, own: row.accountId === own, gap: false,
  }));
  if (you && !out.some((l) => l.own)) out.push({ row: you, own: true, gap: true });
  return out;
}

/** One of the mirrored bars: the reader's figure, the winner's, the gap
 *  between them, and how much of its half each bar fills, in percent of
 *  the larger figure. */
export interface CompareLine {
  label: string;
  you: number;
  winner: number;
  gap: number;
  youPct: string;
  winnerPct: string;
}

/**
 * The figures that both table rows carry, the reader's against the
 * winner's: points, cells taken, fleets sunk, sieges and resources
 * gathered. Only figures of this season stand here (R1523). The winner's
 * score terms are not on the wire, so the bars compare nothing else. A
 * pair of 0 against 0 draws no bar at all (R2089): two empty tracks state
 * nothing.
 */
export function compareLines(you: Standing, winner: Standing): CompareLine[] {
  const pairs: [number, number][] = [
    [you.score, winner.score],
    [you.cells, winner.cells],
    [you.kills, winner.kills],
    [you.sieges, winner.sieges],
    [you.gathered, winner.gathered],
  ];
  const out: CompareLine[] = [];
  pairs.forEach(([a, b], i) => {
    if (a === 0 && b === 0) return;
    const m = Math.max(a, b);
    out.push({
      label: S.compare[i] ?? '', you: a, winner: b, gap: a - b,
      youPct: ((a / m) * 100).toFixed(1), winnerPct: ((b / m) * 100).toFixed(1),
    });
  });
  return out;
}

/**
 * The row the duel sets against the reader: the winner, or the second
 * place when the reader is the winner (R1519). A table of one row gives
 * the winner back, so a lone reader meets themselves.
 */
export function duelOpponent(rows: readonly Standing[], you: Standing): Standing | undefined {
  const winner = rows[0];
  if (winner === undefined || you.accountId !== winner.accountId) return winner;
  return rows[1] ?? winner;
}

/**
 * What stands after the place lines (R2089). The duel stands only when
 * the row the reader meets sails in another faction: a clan mate or a
 * faction mate is never set against the reader. In its place one line
 * names the bond, or says that the reader won.
 */
export type DuelView =
  | { kind: 'duel'; opponent: Standing }
  | { kind: 'won' }
  | { kind: 'mate'; bond: 'clan' | 'faction'; winner: Standing }
  | { kind: 'none' };

export function duelView(rows: readonly Standing[], you: Standing | undefined): DuelView {
  if (!you) return { kind: 'none' };
  const opponent = duelOpponent(rows, you);
  if (!opponent) return { kind: 'none' };
  const won = rows[0]?.accountId === you.accountId;
  const sameClan = you.clanTag !== '' && opponent.clanTag === you.clanTag;
  if (opponent.accountId === you.accountId || (won && (sameClan || opponent.faction === you.faction))) {
    return { kind: 'won' };
  }
  if (sameClan) return { kind: 'mate', bond: 'clan', winner: opponent };
  if (opponent.faction === you.faction) return { kind: 'mate', bond: 'faction', winner: opponent };
  return { kind: 'duel', opponent };
}

/** The reader's score as a whole percentage of the winner's. */
export function winnerShare(you: Standing, winner: Standing): number {
  return winner.score > 0 ? Math.round((you.score / winner.score) * 100) : 0;
}

/** One group of the locked table: a faction or a clan, its summed score,
 *  its rows, and its place among the groups of its kind. */
export interface GroupPlace<K> {
  key: K;
  score: number;
  seats: number;
  rank: number;
}

/**
 * The groups of the locked table by `keyOf`, ranked by their summed
 * score, the rule of the Clan War banners (`hub/seasonsummary.go`
 * `summaryBanners`). A tie goes to the group whose best row stands
 * higher, so the order is the table's own. A row whose key is null
 * belongs to no group.
 */
export function groupPlaces<K>(
  rows: readonly Standing[], keyOf: (r: Standing) => K | null,
): GroupPlace<K>[] {
  const at = new Map<K, { key: K; score: number; seats: number; first: number }>();
  rows.forEach((r, i) => {
    const k = keyOf(r);
    if (k === null) return;
    const g = at.get(k);
    if (g) {
      g.score += r.score;
      g.seats++;
    } else {
      at.set(k, { key: k, score: r.score, seats: 1, first: i });
    }
  });
  return [...at.values()]
    .sort((a, b) => b.score - a.score || a.first - b.first)
    .map((g, i) => ({ key: g.key, score: g.score, seats: g.seats, rank: i + 1 }));
}

/** One line of YOUR PLACE IN THE SEA: a place among `of`. */
export interface PlaceLine {
  rank: number;
  of: number;
  score: number;
}

/** The three places of YOUR PLACE IN THE SEA (R2089). Each is null when
 *  the reader has none: no faction row, no clan, or no line in the table. */
export interface SeaPlaces {
  faction: (PlaceLine & { faction: number }) | null;
  clan: (PlaceLine & { tag: string }) | null;
  you: PlaceLine | null;
}

/**
 * The places of the reader's faction, clan and own row. A Clan War world
 * reads its banners, where each faction is the banner of one clan, so it
 * states no second clan line. Every other world sums the locked table,
 * which the hub sends whole. `faction` is the reader's faction, for a
 * reader the table does not rank.
 */
export function seaPlaces(s: SeasonSummary, faction: number): SeaPlaces {
  const you = s.you;
  const f = you?.faction ?? faction;
  const out: SeaPlaces = {
    faction: null, clan: null,
    you: you ? { rank: you.rank, of: s.rows.length, score: you.score } : null,
  };
  if (s.banners.length > 0) {
    const b = s.banners.find((x) => x.faction === f);
    if (b) out.faction = { faction: f, rank: b.rank, of: s.banners.length, score: b.score };
    return out;
  }
  const factions = groupPlaces(s.rows, (r) => (r.faction > 0 ? r.faction : null));
  const mine = factions.find((g) => g.key === f);
  if (mine) out.faction = { faction: f, rank: mine.rank, of: factions.length, score: mine.score };
  const tag = you?.clanTag ?? '';
  if (tag !== '') {
    const clans = groupPlaces(s.rows, (r) => (r.clanTag !== '' ? r.clanTag : null));
    const c = clans.find((g) => g.key === tag);
    if (c) out.clan = { tag, rank: c.rank, of: clans.length, score: c.score };
  }
  return out;
}

/** The sections the columns hold, in order. */
export type SummaryColumn = 'rankings' | 'yours' | 'terms' | 'achievements';

/**
 * The column split of the sections under the place lines (R1519). The
 * left column holds every ranking: the top of the table and, on a Clan
 * War world, the clan ranking. YOUR SEASON and the score terms follow,
 * and the grades stand alone in the last column, in every mode. A reader
 * the table does not rank has no YOUR SEASON and no score terms. A custom
 * world files no achievement, so its card has no grades column (R2979).
 */
export function summaryColumns(s: SeasonSummary): SummaryColumn[] {
  const cols: SummaryColumn[] = !s.you || !s.stats ? ['rankings'] : ['rankings', 'yours', 'terms'];
  if (!traceFree(s.mode)) cols.push('achievements');
  return cols;
}

/** The statistics of YOUR SEASON, as label and value pairs, in the order
 *  of the record. The captains lost stand in YOU CARRY (R2089). */
export function statLines(you: Standing, st: SeasonSummaryStats): [string, number][] {
  return [
    [S.stat_cells, you.cells],
    [S.stat_kills, you.kills],
    [S.stat_fleets_lost, st.fleetsLost],
    [S.stat_sieges_won, you.sieges],
    [S.stat_sieges_lost, st.siegesLost],
    [S.stat_gathered, st.gathered],
    [S.stat_expeditions, st.expeditions],
    [S.stat_buildings, st.buildingLevels],
    [S.stat_tech, st.techRecovered],
    [S.stat_throne, st.throneHours],
    [S.stat_prisoners, st.prisoners],
    [S.stat_trades, st.trades],
  ];
}

/** How many leading statistics the bars already state: cells and fleets
 *  sunk. YOUR SEASON states the rest. */
const STATS_IN_BARS = 2;

/** The score terms as label and points pairs, in game.ScoreSource order.
 *  A term the build has no label for keeps its place with an empty one. */
export function sourceLines(st: SeasonSummaryStats): [string, number][] {
  return st.scoreBySource.map((v, i) => [S.sources[i] ?? '', v]);
}

/**
 * The captains that cross the reset with the reader (numbers.md §0, §12):
 * every captain of the roster that is not a cadet, because an offer does
 * not survive the reset (`game.AgeCaptains`). The strongest captain comes
 * first, by the sum of the attributes, then the roster order.
 */
export function carryCaptains(roster: readonly Captain[]): Captain[] {
  const sum = (c: Captain): number => c.attrs.reduce((a, b) => a + b, 0);
  return roster
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => !c.cadet)
    .sort((a, b) => sum(b.c) - sum(a.c) || a.i - b.i)
    .map(({ c }) => c);
}

/**
 * What the card knows about the reader's account and the roll (R2089).
 * `swept` is a guest that the roll deletes (`guestwarn.ts sweptAtRoll`),
 * `kept` every other account, and `asking` while the page asks `/api/me`.
 */
export type GuestState = 'swept' | 'kept' | 'asking';

/** One line of ACHIEVEMENTS: the name, and the depth zone of the grade
 *  (0 for a recognition, which carries none; R1552). */
export interface AchievementRow {
  name: string;
  grade: number;
}

/** What the ACHIEVEMENTS section states: its rows and how many lines it
 *  cut, or why it has none. */
export type AchievementView =
  | { kind: 'rows'; rows: AchievementRow[]; more: number }
  | { kind: 'none' | 'loading' | 'failed' };

/**
 * The grades the reader reached this season, from the account's record
 * (R1518): every grade reached at or after the season's start, one line a
 * code at its deepest grade, and the recognitions of this season. The hub
 * counts a grade and the achievement service keeps it, so the record is
 * the one complete answer. A page with no meta server passes `failed`, so
 * the section says that the record is not available (§3.4).
 *
 * The deepest grade comes first, and within one grade the newest line; a
 * recognition carries no grade and comes last (R1519). The card states at
 * most `max` lines, and `more` counts the lines it cut.
 */
export function achievementView(
  record: AccountAchievements | null, failed: boolean,
  startedUnix: number, season: number, max = SEASON_SUMMARY_ACH_ROWS,
): AchievementView {
  if (record === null) return { kind: failed ? 'failed' : 'loading' };
  const best = new Map<string, { grade: number; at: number }>();
  for (const g of record.grades) {
    if (g.reachedAt < startedUnix) continue;
    const held = best.get(g.code);
    if (!held || g.grade > held.grade) {
      best.set(g.code, { grade: g.grade, at: Math.max(g.reachedAt, held?.at ?? 0) });
    }
  }
  const dated: { at: number; row: AchievementRow }[] = [];
  for (const [code, g] of best) {
    dated.push({ at: g.at, row: { name: nameOf(code), grade: zoneOf(rowByCode(code), g.grade) } });
  }
  for (const r of record.recognitions) {
    if (r.season !== season || r.grantedAt < startedUnix) continue;
    dated.push({ at: r.grantedAt, row: { name: nameOf(r.code), grade: 0 } });
  }
  if (dated.length === 0) return { kind: 'none' };
  dated.sort((a, b) => b.row.grade - a.row.grade || b.at - a.at);
  const cut = Math.max(0, max);
  return {
    kind: 'rows', rows: dated.slice(0, cut).map((d) => d.row),
    more: Math.max(0, dated.length - cut),
  };
}

// ---------------------------------------------------------------------------
// The markup
// ---------------------------------------------------------------------------

/** Two decimals: an SVG coordinate needs no more. */
const px = (x: number): number => Number(x.toFixed(2));

/** The hex mark's width to its height, the share of the width where its
 *  flat top begins and ends, and its figure's size to its height: the
 *  mockup's map hex. */
const HEX_ASPECT = 1.15;
const HEX_FLAT_FROM = 0.25;
const HEX_FLAT_TO = 0.75;

/**
 * The rank mark of the top three, `size` px high: a filled hex for the
 * first, a ringed hex for the second and an open, dashed hex for the
 * third, each with its rank in it at `figure` px (R2113). The rank reads by shape and weight in
 * greyscale too. Any other rank has none.
 */
export function medalSvg(rank: number, size: number, figure: number): string {
  if (rank < 1 || rank > 3) return '';
  const h = size;
  const w = px(size * HEX_ASPECT);
  const a = px(w * HEX_FLAT_FROM);
  const b = px(w * HEX_FLAT_TO);
  const pts = `${a},0 ${b},0 ${w},${px(h / 2)} ${b},${h} ${a},${h} 0,${px(h / 2)}`;
  const ink = rank === 1 ? 'var(--gold)' : rank === 2 ? 'var(--text)' : cssColor(0);
  const body = rank === 1 ? `<polygon points="${pts}" style="fill:${ink}"/>`
    : rank === 2 ? `<polygon points="${pts}" style="fill:none;stroke:${ink};stroke-width:2.2"/>`
      : `<polygon points="${pts}" style="fill:none;stroke:${ink};stroke-width:1;stroke-dasharray:2 1.4"/>`;
  const fig = rank === 1 ? 'rgb(var(--deep-rgb))' : ink;
  const text = `<text x="${px(w / 2)}" y="${px(h / 2)}" text-anchor="middle" dominant-baseline="central"`
    + ` style="font-family:var(--mono);font-weight:700;font-size:${figure}px;fill:${fig}">${rank}</text>`;
  return `<svg class="medal" width="${px(w + 2)}" height="${h + 2}" viewBox="-1 -1 ${px(w + 2)} ${h + 2}"`
    + ` aria-hidden="true">${body}${text}</svg>`;
}

/** The grade pips: filled to the grade, out of the six grades. They read
 *  by count in greyscale too; the title names the grade. */
function pips(grade: number): string {
  let h = `<span class="gp" title="${esc(gradeName(grade))}">`;
  for (let i = 1; i <= ACHIEVEMENT_GRADES.length; i++) h += i <= grade ? '◆' : '<i>◇</i>';
  return `${h}</span>`;
}

/** A row's name, with its clan tag. */
function rowName(r: Standing): string {
  return `${r.clanTag ? `[${esc(r.clanTag)}] ` : ''}${esc(r.name)}`;
}

/** The YOU tag after the reader's own name. */
const youTag = (): string => `<span class="you-tag">${S.row_you}</span>`;

/** The rank cell of a row: the medal of the top three, else the figure. */
function rankCell(rank: number): string {
  return `<span class="rk">${rank <= 3 ? medalSvg(rank, SEASON_SUMMARY_MEDAL_PX, SEASON_SUMMARY_MEDAL_FIGURE_PX) : rank}</span>`;
}

/** One row of the top of the table. */
function rankRow(l: RankLine): string {
  const r = l.row;
  const top = r.rank <= 3;
  return `<div class="r${l.own ? ' own' : ''}${l.gap ? ' gap' : ''}">`
    + rankCell(r.rank)
    + `<span class="nm${top ? ' r-top' : ''}" style="color:${l.own ? 'var(--accent)' : cssColor(r.faction)}">`
    + `${rowName(r)}${l.own ? youTag() : ''}</span>`
    + `<span class="num">${groupThousands(r.score)}</span></div>`;
}

/** One banner row of a Clan War world. The name is the faction's name as
 *  the hello's banners renamed it (R1260). */
function bannerRow(b: SeasonSummaryBanner, mine: boolean): string {
  return `<div class="r${mine ? ' own' : ''}">`
    + rankCell(b.rank)
    + `<span class="nm" style="color:${cssColor(b.faction)}">${esc(factionName(b.faction))}`
    + `${b.house ? ` <span class="dl">${S.banner_house}</span>` : ''}${b.house ? '' : cupMarkFor(b.tag, 12)}`
    + `${mine ? youTag() : ''}`
    + ` <span class="dl">${S.banner_seats({ seats: b.seats })}</span></span>`
    + `<span class="num">${groupThousands(b.score)}</span></div>`;
}

/** A label and a figure; a figure of 0 takes the quiet ink (R2089). */
function figureRow(label: string, v: number): string {
  return `<div class="r2${v ? '' : ' zero'}"><span>${label}</span>`
    + `<span class="num">${groupThousands(v)}</span></div>`;
}

/** A section of label and figure rows. A section whose figures are all 0
 *  states one line in their place (R2089). */
function figureSection(head: string, lines: readonly [string, number][]): string {
  let h = `<section><h3>${head}</h3>`;
  if (lines.every(([, v]) => v === 0)) return `${h}<div class="empty">${S.all_zero}</div></section>`;
  for (const [label, v] of lines) h += figureRow(label, v);
  return `${h}</section>`;
}

/**
 * YOU CARRY (R2089): what crosses the reset. The Honour the roll pays
 * stands at the figure step, with the lifetime total after the roll; the
 * captains that cross follow, then the captains lost this season. A guest
 * that the roll deletes reads the guest sentence instead (§3.5, R1565).
 */
export function carryPart(
  s: SeasonSummary, roster: readonly Captain[], guest: GuestState,
  max = SEASON_SUMMARY_CARRY_ROWS,
): string {
  if (guest === 'swept') {
    return `<section class="carry guest"><h3>${G.head}</h3><p>${G.summary}</p></section>`;
  }
  const st = s.stats;
  const you = s.you;
  let h = `<section class="carry"><h3>${S.section_carry}</h3><div class="cy">`;
  // A custom world pays no Honour (R2979), so its card states no figure.
  if (st && you && !traceFree(s.mode)) {
    const war = s.mode === MODE_CLANWAR_ID;
    const fig = guest === 'asking' ? S.carry_asking : S.honour_value({ honour: groupThousands(st.honour) });
    // The lifetime total reads the account's own Honour, which a Clan War
    // does not move: it pays Clan Honour (R1512).
    const total = war || guest === 'asking' ? ''
      : `<span class="sub">${S.carry_honour_total({ total: groupThousands(you.honour + st.honour) })}</span>`;
    h += `<div class="hon"><span class="lab">${war ? S.carry_clan_honour : S.carry_honour}</span>`
      + `<b class="fig">${fig}</b>${total}</div>`;
  }
  const caps = carryCaptains(roster);
  h += `<div class="caps"><span class="lab">${S.carry_captains({ n: caps.length })}</span>`;
  if (caps.length === 0) {
    h += `<div class="empty">${S.carry_no_captain}</div>`;
  } else {
    h += '<ul>';
    for (const c of caps.slice(0, Math.max(0, max))) {
      const risk = c.effects && c.effects.retireRiskPct > 0
        ? `<span class="risk">${S.carry_risk({ pct: Math.round(c.effects.retireRiskPct) })}</span>` : '';
      h += `<li><span class="nm">${esc(c.name)}</span>`
        + `<span class="ph">${esc(O.caprow_phase_age({ phase: phaseWord(c), age: c.age }))}</span>${risk}</li>`;
    }
    h += '</ul>';
    if (caps.length > max) h += `<div class="more">${S.carry_more({ n: caps.length - max })}</div>`;
  }
  if (st) {
    h += `<div class="lost${st.captainsLost > 0 ? ' some' : ''}">`
      + `${st.captainsLost > 0 ? S.captains_lost({ n: st.captainsLost }) : S.carry_lost_none}</div>`;
  }
  return `${h}</div></div></section>`;
}

/** One line of YOUR PLACE IN THE SEA: the kind, the name, the place and
 *  the score. */
function placeRow(kind: string, name: string, ink: string, p: PlaceLine): string {
  return `<div class="pr"><span class="pk">${kind}</span>`
    + `<span class="pn" style="color:${ink}">${name}</span>`
    + `<span class="pp">${S.place({ rank: ordinal(p.rank), of: p.of })}</span>`
    + `<span class="num">${groupThousands(p.score)}</span></div>`;
}

/** The duel, the mirrored bars and the share line (R1519). The winner's
 *  rank stands once, as the ordinal; no medal repeats it (R2089). */
function duelMarkup(you: Standing, winner: Standing): string {
  const second = winner.rank > 1;
  let h = '<div class="duel">'
    + `<div class="who" style="--fc:var(--accent)"><span class="lab">${S.row_you}</span>`
    + `<span class="rk">${ordinal(you.rank)}</span>`
    + `<span class="nm">${rowName(you)}</span></div>`
    + `<div class="vs">${S.vs}</div>`
    + `<div class="who w" style="--fc:${cssColor(winner.faction)}">`
    + `<span class="lab">${second ? S.second : S.winner}</span>`
    + `<span class="rk">${ordinal(winner.rank)}</span>`
    + `<span class="nm">${rowName(winner)}</span></div></div>`;
  h += '<div class="mx">';
  for (const l of compareLines(you, winner)) {
    h += `<span class="num yv">${groupThousands(l.you)}</span>`
      + `<div class="bar y"><i style="width:${l.youPct}%"></i></div>`
      + `<div class="mid">${l.label}<b>${signedFigure(l.gap)}</b></div>`
      + `<div class="bar w"><i style="width:${l.winnerPct}%"></i></div>`
      + `<span class="num wv">${groupThousands(l.winner)}</span>`;
  }
  const share = (second ? S.share_second : S.share)({ share: `<b>${S.share_pct({ pct: winnerShare(you, winner) })}</b>` });
  return `${h}</div><div class="gapline">${share}</div>`;
}

/**
 * YOUR PLACE IN THE SEA (R2089): the places of the reader's faction, clan
 * and own row, then the duel when the row the reader meets sails in
 * another faction, or one line in its place. A reader the table does not
 * rank reads that line in the place of the own row.
 */
export function placePart(s: SeasonSummary, faction: number): string {
  const p = seaPlaces(s, faction);
  let h = `<section class="place"><h3>${S.section_place}</h3><div class="pl">`;
  if (p.faction) {
    h += placeRow(S.place_faction, esc(factionName(p.faction.faction)), cssColor(p.faction.faction), p.faction);
  }
  if (p.clan) h += placeRow(S.place_clan, `[${esc(p.clan.tag)}]`, 'var(--text)', p.clan);
  if (p.you && s.you) h += placeRow(S.place_you, rowName(s.you), 'var(--accent)', p.you);
  h += '</div>';
  // The road builder of the faction (v2-the-deep.md §7.2 rule 11, R3074).
  if (s.roadBuilder) h += `<div class="bond">${esc(S.road_builder({ name: s.roadBuilder, h: groupThousands(s.roadBuilderHours) }))}</div>`;
  if (!s.you) return `${h}<div class="empty">${S.you_unranked}</div></section>`;
  const d = duelView(s.rows, s.you);
  if (d.kind === 'duel') h += duelMarkup(s.you, d.opponent);
  else if (d.kind === 'won') h += `<div class="bond">${S.you_won}</div>`;
  else if (d.kind === 'mate') {
    const line = d.bond === 'clan' ? S.winner_clan : S.winner_faction;
    h += `<div class="bond">${line({ name: rowName(d.winner) })}</div>`;
  }
  return `${h}</section>`;
}

/** The sections in columns, under the place lines. */
function columnsPart(s: SeasonSummary, ach: AchievementView): string {
  const you = s.you;
  const st = s.stats;
  let html = '<div class="cols">';
  for (const col of summaryColumns(s)) {
    if (col === 'rankings') {
      html += '<div class="col"><section class="top">';
      html += `<h3>${S.section_top({ rows: SEASON_SUMMARY_RANK_ROWS })}</h3>`;
      for (const l of rankLines(s.rows, you)) html += rankRow(l);
      html += '</section>';
      // The clan ranking stands under the table in the same column
      // (R1519), so the grades get a column of their own.
      if (s.banners.length > 0) {
        const tag = you?.clanTag ?? '';
        html += `<section class="clans"><h3 class="next">${S.section_banners}</h3>`;
        for (const b of s.banners) html += bannerRow(b, tag !== '' && b.tag === tag);
        html += '</section>';
      }
      html += '</div>';
    } else if (col === 'yours' && you && st) {
      html += figureSection(S.section_you, statLines(you, st).slice(STATS_IN_BARS));
    } else if (col === 'terms' && st) {
      html += figureSection(S.score_by_source, sourceLines(st));
    } else if (col === 'achievements') {
      html += `<section><h3>${S.section_achievements}</h3>`;
      if (ach.kind === 'rows') {
        for (const a of ach.rows) {
          html += `<div class="r2"><span>${esc(a.name)}</span>${a.grade > 0 ? pips(a.grade) : ''}</div>`;
        }
        if (ach.more > 0) html += `<div class="more">${S.ach_more({ n: ach.more })}</div>`;
      } else {
        const why = ach.kind === 'none' ? S.ach_none
          : ach.kind === 'loading' ? S.ach_loading : S.ach_no_record;
        html += `<div class="empty">${why}</div>`;
      }
      html += '</section>';
    }
  }
  return `${html}</div>`;
}

/** What the body markup reads beyond the summary: the roster, the
 *  reader's faction and the state of the guest question. */
export interface BodyCtx {
  roster: readonly Captain[];
  faction: number;
  guest: GuestState;
}

/** The markup of the body before the timelapse: YOU CARRY, the place in
 *  the sea and the sections in columns, in that order (R2089). */
export function bodyMarkup(
  s: SeasonSummary, ach: AchievementView,
  ctx: BodyCtx = { roster: [], faction: 0, guest: 'kept' },
): string {
  return carryPart(s, ctx.roster, ctx.guest) + placePart(s, ctx.faction) + columnsPart(s, ach);
}

/** The italic face of "against": the vendored Fell face the chart already
 *  declares (`client/entries/index.html`), and its fallback. */
const FELL_FAMILY = [CHART.text.fellFamily, ...CHART.text.fallback]
  .map((f) => (f.includes(' ') ? `"${f}"` : f)).join(', ');

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

/** What the card needs from the page. */
export interface SeasonSummaryDeps {
  /** The chrome root `#ui`: the card stands in its stacking context. */
  host: HTMLElement;
  state: GameState;
  /** MENU: back to the hub, through the dock's own path (R1203). */
  menu: () => void;
  /** The achievement record this session holds, and whether its call
   *  failed or the page has no meta server (`ui/achievements.ts`). */
  record: () => AccountAchievements | null;
  recordFailed: () => boolean;
  /** Asks the world socket for the season timelapse (R1620). */
  queryTimelapse: () => void;
  /** True when the last press on the chrome became a title hold (R1555). */
  titleHeld?: () => boolean;
  /** Whether the season roll deletes this account: a guest that the
   *  sweep of R607 finds. Absent on a page with no meta server, where no
   *  account is swept (R2089). */
  sweptAtRoll?: () => Promise<boolean>;
  /** REGISTER of a swept guest: the world goes down and the hub opens on
   *  the ACCOUNT page, as the guest card's REGISTER does (§3.5). */
  register?: () => void;
}

/** How often the open card redraws its countdown and its body, in ms: the
 *  countdown shows whole seconds. */
const TICK_MS = 1000;

/** The countdown figure in the accent. */
const accent = (t: string): string => `<b>${t}</b>`;

export class SeasonSummaryCard {
  private el: HTMLElement | null = null;
  private footHost: MarkupHost | null = null;
  private bodyHost: MarkupHost | null = null;
  private registerEl: HTMLButtonElement | null = null;
  private route: PressRoute | null = null;
  private timer = 0;
  private drawn: SeasonSummary | null = null;
  /** The answer to the guest question of this card (R2089). */
  private guest: GuestState = 'kept';
  /** The season timelapse under the sections (card 07 of round 076,
   *  R1620). The card keeps it while it stands, so its play outlives a
   *  redraw. */
  private readonly timelapse: SummaryTimelapse;

  constructor(private readonly d: SeasonSummaryDeps) {
    this.timelapse = new SummaryTimelapse({
      state: d.state, query: d.queryTimelapse, titleHeld: d.titleHeld,
    });
    d.state.on(() => this.sync());
  }

  /** Puts the card on the glass or takes it off, after a state change. */
  private sync(): void {
    const s = this.d.state;
    const want = s.summary !== null && s.summaryShown;
    if (!want) {
      this.close();
      return;
    }
    if (!this.el) this.open();
    else if (this.drawn !== s.summary) this.draw();
    this.timelapse.sync();
  }

  private open(): void {
    const root = document.createElement('div');
    root.id = 'seasonsummary';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-labelledby', 'seasonsummary-head');
    root.style.setProperty('--summary-fell', FELL_FAMILY);
    // Reduced motion is a class, as on the season banner: the one place
    // that asks the OS is render/motion.ts.
    if (reducedMotion()) root.classList.add('still');
    // The head is a reprise of the loading screen art (R2111): the
    // kicker, the hero, the glow rule and the clock line. The clock stands
    // there alone, never again in the footer (R2089).
    root.innerHTML = `<div class="card panel">`
      + `<header class="bn"><div class="kick"></div><div class="hero" id="seasonsummary-head"></div>`
      + `<div class="rule"></div><div class="foot"></div></header>`
      + `<div class="body"></div>`
      + `<footer><button type="button" class="reg" data-ss="register" hidden>${G.register}</button>`
      + `<button type="button" data-ss="close">${S.btn_close}</button>`
      + `<button type="button" class="primary" data-ss="menu">${S.btn_menu}</button></footer>`
      + `</div>`;
    const foot = root.querySelector<HTMLElement>('.foot');
    this.footHost = foot ? new MarkupHost(foot) : null;
    this.registerEl = root.querySelector('button[data-ss="register"]');
    // The body holds the markup part and the season timelapse after it.
    // The markup redraws on its own guard, and never the timelapse.
    const body = root.querySelector('.body');
    const bodyEl = document.createElement('div');
    this.bodyHost = new MarkupHost(bodyEl);
    body?.append(bodyEl, this.timelapse.el);
    // The buttons stand in the card's fixed footer and not in the body
    // that scrolls, so they act on the press, and Enter and Space act
    // through their click (§16.8, R1206, R1569).
    this.route = new PressRoute(root, {
      control: 'button[data-ss]', scrolls: () => false, act: (b) => this.act(b),
    });
    // esc closes the card to the map, as VIEW MAP does (R2089). The map's
    // own key handler stands down under the chrome veil.
    window.addEventListener('keydown', this.onKey, true);
    this.d.host.appendChild(root);
    this.el = root;
    this.askGuest();
    this.timelapse.open();
    this.draw();
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  /** Asks one time a card whether the roll deletes this account. Until
   *  the answer the Honour figure reads `…`, so a guest never reads an
   *  Honour the roll takes away (R2089). */
  private askGuest(): void {
    const ask = this.d.sweptAtRoll;
    if (!ask) {
      this.guest = 'kept';
      return;
    }
    this.guest = 'asking';
    void ask().catch(() => false).then((swept) => {
      if (!this.el) return;
      this.guest = swept ? 'swept' : 'kept';
      this.tick();
    });
  }

  private close(): void {
    if (!this.el) return;
    window.clearInterval(this.timer);
    window.removeEventListener('keydown', this.onKey, true);
    this.route?.destroy();
    this.route = null;
    this.timelapse.close();
    this.el.remove();
    this.el = null;
    this.footHost = null;
    this.bodyHost = null;
    this.registerEl = null;
    this.drawn = null;
  }

  /** Writes the banner and the body of the summary in the store. */
  private draw(): void {
    const s = this.d.state.summary;
    const el = this.el;
    if (!s || !el) return;
    this.drawn = s;
    const kick = el.querySelector('.kick');
    const hero = el.querySelector('.hero');
    if (kick) kick.textContent = kickLine(s.mode, this.d.state.hello?.worldName ?? '');
    if (hero) hero.textContent = B.hero_season({ season: s.season });
    el.classList.toggle('held', s.held);
    this.tick();
  }

  /** The countdown, and the body when its markup changed: the record of
   *  achievements, the roster and the guest answer land on their own
   *  clocks. */
  private tick(): void {
    const st = this.d.state;
    const s = st.summary;
    if (!s || !this.footHost || !this.bodyHost) return;
    const left = nextSeasonLeftSec(s, st.summaryAtMs, st.settlementEndsMs, Date.now());
    const clock = clockLine(left, accent);
    this.footHost.write(clock);
    const ach = achievementView(
      this.d.record(), this.d.recordFailed(), Number(s.seasonStartedUnix), s.season,
    );
    const body = bodyMarkup(s, ach, {
      roster: st.empire?.captains ?? [], faction: st.faction, guest: this.guest,
    });
    this.bodyHost.write(body);
    if (this.registerEl) this.registerEl.hidden = this.guest !== 'swept' || !this.d.register;
  }

  private act(b: HTMLElement): void {
    if (b.dataset.ss === 'menu') this.d.menu();
    else if (b.dataset.ss === 'close') this.d.state.showSummary(false);
    else if (b.dataset.ss === 'register') this.d.register?.();
  }

  /** esc, the map's `escape` row of `[keys.map]`: the card goes away and
   *  the map stays, as VIEW MAP does. A text field keeps its own esc. */
  private readonly onKey = (ev: KeyboardEvent): void => {
    const tag = (ev.target as HTMLElement | null)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || !matches(KEYS.map.escape, ev)) return;
    ev.preventDefault();
    ev.stopPropagation();
    this.d.state.showSummary(false);
  };
}
